import {
  NotificationOption,
  NotificationType,
} from 'features/notifications/notifications.types';
import { Topic } from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useTopicSubscriptions } from 'hooks/useTopicSubscriptions';
import { useNotifications } from 'hooks/useNotifications';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import { NotificationSettingTypes } from '../../../../constants';

type UseWildfireNotifications = {
  isSubscribedToNotifications: boolean;
  showSubscribeToSilentNotifications: boolean;
  toggleNotificationsSubscription: () => void;
  subscribeToSilentNotifications: () => void;
};

export const OPT_IN_TOPIC_TYPE = 'incident_topic';
export const OPT_OUT_TOPIC_TYPE = 'geo_event_opt_out';

const getTopicByType = (topics: Topic[], type: string): Topic | undefined => {
  return topics.find((topic) => topic.type === type);
};

export const isSubscribed = (
  hasOptOut: boolean,
  hasOptIn: boolean,
  geoEvent: Wildfire,
  regionSetting: NotificationOption | null,
): boolean => {
  const {
    data: { isPrescribed },
    notificationType,
  } = geoEvent;
  if (
    isPrescribed ||
    !regionSetting ||
    regionSetting === NotificationSettingTypes.off
  ) {
    return hasOptIn;
  }
  if (regionSetting === NotificationSettingTypes.allWithSilent) {
    return !hasOptOut;
  }
  if (
    regionSetting === NotificationSettingTypes.onlyNewWithSilent ||
    regionSetting === NotificationSettingTypes.onlyNew
  ) {
    return hasOptIn;
  }
  if (
    regionSetting === NotificationSettingTypes.all &&
    notificationType === NotificationType.Silent
  ) {
    return hasOptIn;
  }
  if (regionSetting === NotificationSettingTypes.all) {
    return !hasOptOut;
  }
  // shouldn't get here
  return false;
};

export const useWildfireNotifications = (
  wildfire: Wildfire,
): UseWildfireNotifications => {
  const { t } = useTranslation();
  const { subscribeToTopic, unsubscribe, getSubscriptionToTopic } =
    useTopicSubscriptions();
  const { getMostCoverageSetting, putNotificationSettings } =
    useNotifications();

  const regionSetting = getMostCoverageSetting(wildfire.regions);
  const anyRegionSubscription =
    Boolean(regionSetting) && regionSetting !== NotificationSettingTypes.off;

  // These topics are guaranteed to exist in the GeoEvent model but not in the QA environment
  const optInTopic = getTopicByType(wildfire.topics, OPT_IN_TOPIC_TYPE);
  const optOutTopic = getTopicByType(wildfire.topics, OPT_OUT_TOPIC_TYPE);

  const optInTopicSubscription = optInTopic
    ? getSubscriptionToTopic(optInTopic.id)
    : null;
  const optOutTopicSubscription = optOutTopic
    ? getSubscriptionToTopic(optOutTopic.id)
    : null;

  const subscribedToNotifications = isSubscribed(
    Boolean(optOutTopicSubscription),
    Boolean(optInTopicSubscription),
    wildfire,
    regionSetting,
  );

  /**
   * Track subscription status in useState() here to optimistically reflect user
   * actions, but then reset it to the real value, determined by isSubscribed(),
   * whenever it changes (using the useEffect()).
   */
  const [isSubscribedOptimistic, setIsSubscribedOptimistic] = useState(
    subscribedToNotifications,
  );

  useEffect(() => {
    setIsSubscribedOptimistic(subscribedToNotifications);
  }, [subscribedToNotifications]);

  const enableNotifications = (): void => {
    // Optimistic updates
    setIsSubscribedOptimistic(true);

    enqueueSnackbar(
      <Typography variant="body2">
        <Trans
          i18nKey="geoEvent.notifications.successMessage.on"
          values={{ wildfireName: wildfire.name }}
        >
          Notifications <b>turned on</b> for the <b>Cloverdale Fire</b>
        </Trans>
      </Typography>,
      { variant: 'success' },
    );

    if (optOutTopicSubscription) {
      // This condition is met when the user has subscribed to the notifications of the
      // county where the wildfire is located, and they have previously turned off
      // notifications for this specific wildfire.
      unsubscribe.mutate(optOutTopicSubscription.id, {
        onError: () => {
          setIsSubscribedOptimistic(false);
        },
      });
    } else if (optInTopic) {
      // Subscribe to the wildfire opt-in topic - turn on notifications
      subscribeToTopic.mutate(optInTopic.id, {
        onError: () => {
          setIsSubscribedOptimistic(false);
        },
      });
    }
  };

  const disableNotifications = (): void => {
    // Optimistic updates
    setIsSubscribedOptimistic(false);

    enqueueSnackbar(
      <Typography variant="body2">
        <Trans
          i18nKey="geoEvent.notifications.successMessage.off"
          values={{ wildfireName: wildfire.name }}
        >
          Notifications <b>turned off</b> for the <b>Cloverdale Fire</b>
        </Trans>
      </Typography>,
      { variant: 'success' },
    );

    if (optInTopicSubscription) {
      // This condition is met when the user has not subscribed to the notifications of the
      // county where the wildfire is located and they have previously turn on
      // notifications for this specific wildfire.
      unsubscribe.mutate(optInTopicSubscription.id, {
        onError: () => {
          setIsSubscribedOptimistic(true);
        },
      });
    } else if (optOutTopic) {
      // Subscribe to the wildfire opt-out topic - turn off notifications
      subscribeToTopic.mutate(optOutTopic.id, {
        onError: () => {
          setIsSubscribedOptimistic(true);
        },
      });
    }
  };

  const toggleNotificationsSubscription = (): void => {
    if (isSubscribedOptimistic) {
      disableNotifications();
    } else {
      enableNotifications();
    }
  };

  const subscribeToSilentNotifications = (): void => {
    // this should only be used if there is a single region
    if (regionSetting === NotificationSettingTypes.off) return;
    const newSetting =
      regionSetting === NotificationSettingTypes.all
        ? NotificationSettingTypes.allWithSilent
        : NotificationSettingTypes.onlyNewWithSilent;
    putNotificationSettings.mutate(
      [
        {
          region: wildfire.regions[0],
          setting: newSetting,
        },
      ],
      {
        onSuccess: () => {
          enqueueSnackbar(t('geoEvent.notifications.successMessage.silent'), {
            variant: 'success',
          });
        },
      },
    );
  };

  const showSubscribeToSilentNotifications =
    wildfire.regions.length === 1 &&
    anyRegionSubscription &&
    wildfire.notificationType === NotificationType.Silent &&
    !wildfire.data.isPrescribed &&
    !(
      regionSetting === NotificationSettingTypes.allWithSilent ||
      regionSetting === NotificationSettingTypes.onlyNewWithSilent
    );

  return {
    isSubscribedToNotifications: isSubscribedOptimistic,
    showSubscribeToSilentNotifications,
    toggleNotificationsSubscription,
    subscribeToSilentNotifications,
  };
};
